/**
 * Dynamic lesson loader — thin async layer over the static lesson registry.
 *
 * HOW TO SWITCH TO FIREBASE STORAGE:
 *   Replace step 3 (the fetch() call below) with a Firebase Storage getDownloadURL call:
 *
 *     import { getStorage, ref, getDownloadURL } from 'firebase/storage';
 *     const storage = getStorage();
 *     const url = await getDownloadURL(ref(storage, `lessons/${lessonId}.json`));
 *     const res = await fetch(url);
 *     const data = await res.json();
 *
 *   Everything else — cache check, static registry fallback, null warning — stays identical.
 *   Remove the /public/lessons/ folder and the local fetch() line once Firebase is live.
 */

import { findGuidedLessonById } from './guidedLessonRegistry';

/** In-memory cache for dynamically loaded lessons (cleared on hard refresh). */
const cache = new Map();

/**
 * Fetch a guided lesson by its lessonId.
 *
 * Resolution order:
 *   1. In-memory cache     — instant return for repeated visits within a session.
 *   2. Static registry     — covers all pre-bundled lessons with no network call.
 *   3. /lessons/{id}.json  — drop JSON files in public/lessons/ for local dev;
 *                            swap this step for Firebase Storage getDownloadURL in production.
 *   4. null                — lesson not found; a console warning is emitted.
 *
 * The function signature is intentionally Firebase-ready: lessonId is the stable key used
 * both as the Storage object path and as the registry key.
 *
 * @param {string} lessonId
 * @returns {Promise<object|null>}
 */
export async function fetchLesson(lessonId) {
  if (!lessonId) return null;

  // 1. In-memory cache
  if (cache.has(lessonId)) {
    return cache.get(lessonId);
  }

  // 2. Static registry (all lessons pre-bundled at build time)
  const staticLesson = findGuidedLessonById(lessonId);
  if (staticLesson) {
    cache.set(lessonId, staticLesson);
    return staticLesson;
  }

  // 3. Local JSON file from public/lessons/ (replace with Firebase Storage for production)
  const localUrl = `/lessons/${lessonId}.json`;
  try {
    const res = await fetch(localUrl);
    if (res.ok) {
      const data = await res.json();
      cache.set(lessonId, data);
      return data;
    }
  } catch {
    // Network error or JSON parse failure — fall through to null
  }

  // 4. Not found
  console.warn(`[lessonLoader] Lesson "${lessonId}" not found in registry or at ${localUrl}`);
  return null;
}
